// components/minimal/ThemedPageContainer.tsx
//
// Runtime seam between the classic PageContainer and the new
// MinimalPageContainer. Every page already imports PageContainer
// from '../../components/layout/PageContainer'; that re-export
// can flip to this themed version with no per-page rewrites once
// we're confident in the minimal variant.
//
// For now, classic users get the original shell + classic NavMenu
// (no change). Minimal users get MinimalPageContainer + the
// minimal nav (when it lands in commit C). The flag itself comes
// from useUITheme() which reads /users/{uid}/uiTheme.

import { ReactElement } from 'react';
import { useUITheme } from '../../hooks/useUITheme';
// CRITICAL: import the classic PageContainer from its file path,
// NOT from '../layout/PageContainer' (the barrel). The barrel
// re-exports THIS very component as `PageContainer`, so going
// through it creates an infinite render loop on the classic
// branch — every <ClassicPageContainer> would resolve back to
// <ThemedPageContainer> and the browser freezes the moment a
// user toggles their uiTheme back to 'classic' (or loads any
// classic-defaulted page after a deploy). The direct file path
// breaks the cycle.
import {
  PageContainer as ClassicPageContainer,
  type PageContainerProps,
} from '../layout/PageContainer/PageContainer';
import { MinimalPageContainer } from './MinimalPageContainer';
import { MinimalNavMenu } from './MinimalNavMenu';

export function ThemedPageContainer(props: PageContainerProps): ReactElement {
  const { isMinimal } = useUITheme();

  if (!isMinimal) {
    return <ClassicPageContainer {...props} />;
  }

  // Minimal mode: hand the minimal container an explicit nav slot
  // so it stays nav-agnostic. titleGradient + gradientColors are
  // dropped on the floor — the minimal title is always solid
  // white and the prop names exist only for classic-shell compat.
  const { user, titleGradient, gradientColors, ...rest } = props;
  void titleGradient;
  void gradientColors;

  return (
    <MinimalPageContainer
      {...rest}
      user={user}
      nav={user ? <MinimalNavMenu user={user} /> : null}
    />
  );
}

export default ThemedPageContainer;
